/* trafficIQ — GA4 website analytics. Four tabs (Overview, AI Search, Pages &
   Audience, Insights) over a shared toolbar. Overview / Pages / AI-Assistant
   sources render from live GA4; the parts that depend on unshipped Google
   features (Search Console generative report, GA4 predictive/anomaly Insights)
   render the designed fallback states. Tab / range / segment persist in the URL. */
import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Select, Stat, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { LeadDrawer } from '@/components/LeadDrawer';
import { STATUS_META, type LeadStatus } from '@/data/status';
import { useClientAccess } from '@/auth/useClientAccess';
import { getAiCrawl, getLeadPages, getLeads, getSearchConsole, getTraffic, getTrafficInsights, getWhatChanged, setLeadStatus, setLeadValue, qk, type AiCrawlReport, type CompareBasis, type LeadPageStat, type SearchPayload, type TrafficInsights as TrafficInsightsData, type TrafficPayload, type WhatChanged } from '@/data/client';

type LeadItem = Awaited<ReturnType<typeof getLeads>>[number];

// Friendly label from a URL path: last segment, title-cased. '/' → 'Home'.
function pageLabel(path: string): string {
  if (path === '/' || !path) return 'Home';
  const seg = path.split('/').filter(Boolean).pop() ?? path;
  return seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const RANGE_OPTS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
];
const SEGMENT_OPTS = [
  { value: 'all', label: 'All traffic' },
  { value: 'organic', label: 'Organic Search' },
  { value: 'paid', label: 'Paid Search' },
  { value: 'ai', label: 'AI Assistant' },
];
const COMPARE_OPTS = [
  { value: 'prev', label: 'Compare: previous period' },
  { value: 'yoy', label: 'Compare: year over year' },
  { value: 'off', label: 'Compare: off' },
];
type CompareMode = 'off' | 'prev' | 'yoy';
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'ai', label: 'AI Search' },
  { value: 'pages', label: 'Pages & Audience' },
  { value: 'insights', label: 'Insights' },
];

/* ---- formatting ------------------------------------------------------- */
const int = (n: number) => Math.round(n).toLocaleString('en-US');
const pct = (n: number) => `${n.toFixed(1)}%`;
const money2 = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
function dur(seconds: number): string {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
function pctDelta(cur: number, prev: number): { text: string; up: boolean } | null {
  if (!prev) return null;
  const d = ((cur - prev) / prev) * 100;
  return { text: `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}%`, up: d >= 0 };
}
function minsAgo(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m ago`;
}
function fmtProperty(id?: string): string {
  const d = (id ?? '').replace(/\D/g, '');
  return d ? d.replace(/(\d{3})(?=\d)/g, '$1-') : (id ?? '');
}

/* ---- small pieces ----------------------------------------------------- */
function Delta({ text, up }: { text: string; up: boolean }) {
  return (
    <span className={`tq-delta ${up ? 'is-up' : 'is-down'}`}>
      <Icon name={up ? 'arrow-up-right' : 'arrow-down-right'} size={13} />{text}
    </span>
  );
}
function Bar({ pct: p, tone }: { pct: number; tone: string }) {
  return <span className="tq-bar"><i className={`tq-bar__fill tone-${tone || 'other'}`} style={{ width: `${Math.max(0, Math.min(100, p))}%` }} /></span>;
}
function Sparkline({ data, tone, startLabel, endLabel }: { data: number[]; tone: 'blue' | 'teal'; startLabel: string; endLabel: string }) {
  if (data.length < 2) return <div className="tq-spark" aria-hidden="true" />;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const xAt = (i: number) => (i / (data.length - 1)) * 100;
  const yAt = (d: number) => 30 - ((d - min) / range) * 26 - 2;
  const pts = data.map((d, i) => `${xAt(i)},${yAt(d)}`).join(' ');
  const area = `0,30 ${pts} 100,30`;
  const last = data[data.length - 1];
  const first = data[0];
  return (
    <div className="tq-chart">
      <div className="tq-chart__plot">
        {/* value range along the y-axis */}
        <span className="tq-chart__y tq-chart__y--max">{int(max)}</span>
        <span className="tq-chart__y tq-chart__y--min">{int(min)}</span>
        <svg className={`tq-spark tone-${tone}`} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
          <polygon className="tq-spark__area" points={area} stroke="none" />
          <polyline points={pts} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
          <circle className="tq-spark__dot" cx={xAt(data.length - 1)} cy={yAt(last)} r="2.5" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className="tq-legend">
        <span><em>{startLabel}</em><b>{int(first)}</b></span>
        <span className="tq-legend__end"><em>{endLabel}</em><b>{int(last)}</b></span>
      </div>
    </div>
  );
}
/* Objective, positive-toned analysis of the traffic in the selected range —
   which sources (incl. AI assistants) contribute traffic + leads, service-area
   reach, mobile, pages, and AI-search. Descriptive only — no recommendations.
   US visitors only. Cached 24h; Refresh forces a fresh pass. */
function TrafficInsights({ range }: { range: string }) {
  const qc = useQueryClient();
  const key = [...qk.trafficInsights, range];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getTrafficInsights(range), staleTime: 5 * 60_000 });
  const refresh = useMutation({ mutationFn: () => getTrafficInsights(range, true), onSuccess: (d) => qc.setQueryData(key, d) });
  const d = data as TrafficInsightsData | undefined;

  if (isLoading) return <Card padding="lg" className="ti-panel"><p className="dash-muted">Analyzing traffic…</p></Card>;
  if (!d || !d.configured) return null;
  if (d.error) return (
    <Card padding="lg" className="ti-panel">
      <p className="dash-muted">{d.error.includes('ANTHROPIC') ? 'The AI key isn’t configured on the Worker.' : 'Couldn’t analyze traffic right now — try Refresh.'}</p>
    </Card>
  );

  return (
    <Card padding="lg" className="ti-panel">
      <div className="ti-head">
        <div className="ti-title">
          <span className="ti-mark"><Icon name="sparkles" size={16} /></span>
          <div><h2>AI traffic analysis</h2><p className="dash-muted">Where your traffic &amp; leads come from · U.S. visitors</p></div>
        </div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {d.summary && <p className="ti-summary">{d.summary}</p>}

      {d.seasonalNote && (
        <div className="ti-seasonal">
          <Icon name="calendar" size={15} />
          <p>{d.seasonalNote}</p>
        </div>
      )}

      {(d.sources?.length ?? 0) > 0 && (
        <div className="ti-section">
          <h3 className="ti-section__title">Where your leads come from</h3>
          <ul className="ti-list">
            {d.sources!.map((s, i) => (
              <li key={i}>
                <span className="ti-list__name">{s.name}</span>
                <span className="ti-list__role">{s.role}</span>
                <span className="ti-list__stat">{s.stat}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.generatedAt && <p className="ti-stamp">Analysis from {minsAgo(d.generatedAt)} · updates daily · Refresh for the latest</p>}
    </Card>
  );
}

function Empty({ icon = 'bar-chart', title, children }: { icon?: string; title: string; children: ReactNode }) {
  return (
    <div className="tq-empty">
      <span className="tq-empty__icon"><Icon name={icon} size={22} /></span>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

const EVENT_ICON: Record<string, string> = {
  phone_call_click: 'phone',
  estimate_form_submit: 'mail',
  chat_started: 'quote',
  book_online: 'calendar',
};
const DEVICE_ICON: Record<string, string> = { mobile: 'smartphone', desktop: 'monitor', tablet: 'tablet' };

function csvDownload(name: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const body = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---- tabs ------------------------------------------------------------- */
function OverviewTab({ d, live, range }: { d: TrafficPayload; live: boolean; range: string }) {
  const k = d.kpis!;
  const sDelta = pctDelta(k.sessions.cur, k.sessions.prev);
  const cDelta = pctDelta(k.conversions.cur, k.conversions.prev);
  const eDeltaPts = k.engagementRate.prev ? (k.engagementRate.cur - k.engagementRate.prev) * 100 : null;
  const crCur = k.sessions.cur ? (k.conversions.cur / k.sessions.cur) * 100 : 0;
  const crPrev = k.sessions.prev ? (k.conversions.prev / k.sessions.prev) * 100 : 0;
  const crDeltaPts = crPrev ? crCur - crPrev : null;
  const channels = d.channels ?? [];
  const chMax = Math.max(1, ...channels.map((c) => c.sessions));
  const cities = d.cities ?? [];
  /* Two views over one query. The default is unchanged — top 6 by sessions —
     because a client with four leads all quarter should not land on a near-empty
     list. The lead view is opt-in and shows EVERY city that converted, which is
     the thing the old 6-row cap made invisible: a suburb with three sessions and
     one call sits nowhere near the top by traffic. */
  const [cityView, setCityView] = useState<'traffic' | 'leads'>('traffic');
  const leadCities = cities.filter((c) => c.conv > 0);
  const shownCities = cityView === 'leads' ? leadCities : cities.slice(0, 6);
  const cityMax = Math.max(1, ...shownCities.map((c) => c.sessions));
  /* These rows will not add up to the GA4 Conversions KPI, and a client who
     tries WILL notice — Drain Pro showed 57 against 20 here. GA4 withholds
     small geo rows whose user counts could identify someone (data thresholding,
     which applies to geo far harder than to channel — the Channels card sums to
     the total exactly). Anything past our 50-row fetch is missing too.
     So the gap is shown rather than left to be discovered. */
  const cityConv = cities.reduce((n, c) => n + c.conv, 0);
  const unattributed = Math.max(0, (k.conversions.cur || 0) - cityConv);
  const events = d.events ?? [];
  const cmpLabel = d.compare === 'yoy' ? 'vs. same period last year' : 'vs. previous period';

  return (
    <div className="dash-stack">
      <div className="dash-kpis dash-kpis--5">
        <Card padding="lg"><Stat label="Sessions" value={int(k.sessions.cur)} sub={live ? cmpLabel : undefined} />{live && sDelta && <Delta text={sDelta.text} up={sDelta.up} />}</Card>
        <Card padding="lg"><Stat label="Engagement rate" value={pct(k.engagementRate.cur * 100)} sub="engaged sessions / sessions" />{live && eDeltaPts != null && <Delta text={`${eDeltaPts >= 0 ? '+' : '−'}${Math.abs(eDeltaPts).toFixed(1)} pts`} up={eDeltaPts >= 0} />}</Card>
        <Card padding="lg"><Stat label="GA4 Conversions" value={int(k.conversions.cur)} sub="forms, calls & chats" info="Google Analytics' count of on-site conversion events. It counts events (not verified people) and can miss phone calls when there's no call tracking — so it won't match Captured Leads in leadIQ." />{live && cDelta && <Delta text={cDelta.text} up={cDelta.up} />}</Card>
        <Card padding="lg"><Stat label="GA4 conversion rate" value={pct(crCur)} sub="conversions / sessions" info="GA4 Conversions ÷ GA4 sessions. Based on Google Analytics events, not Captured Leads." />{live && crDeltaPts != null && <Delta text={`${crDeltaPts >= 0 ? '+' : '−'}${Math.abs(crDeltaPts).toFixed(1)} pts`} up={crDeltaPts >= 0} />}</Card>
        <Card padding="lg"><Stat label="Cost per lead" value={k.costPerLead.cur != null ? money2(k.costPerLead.cur) : '—'} sub="spend / GA4 conversions" info="Ad spend ÷ GA4 Conversions (not Captured Leads) — many leads lack call tracking, so dividing by GA4 conversions gives a truer per-conversion cost." /></Card>
      </div>

      <TrafficInsights range={range} />

      <div className="tq-three">
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>Sessions</h2><p>Last 12 weeks, all channels</p></div></div>
          {(d.trendSessions?.length ?? 0) >= 2
            ? <Sparkline data={d.trendSessions!} tone="blue" startLabel="12 weeks ago" endLabel="This week" />
            : <Empty icon="trending-up" title="Not enough history">A few weeks of data are needed before the trend renders.</Empty>}
        </Card>
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>New users</h2><p>Last 12 weeks, first-time visitors</p></div></div>
          {(d.trendNewUsers?.length ?? 0) >= 2
            ? <Sparkline data={d.trendNewUsers!} tone="teal" startLabel="12 weeks ago" endLabel="This week" />
            : <Empty icon="users" title="Not enough history">A few weeks of data are needed before the trend renders.</Empty>}
        </Card>
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>AI Assistant sessions</h2><p>Since GA4 began classifying the channel</p></div><Badge variant="accent" dot>New channel</Badge></div>
          {(d.trendAi?.length ?? 0) >= 2
            ? <Sparkline data={d.trendAi!} tone="teal" startLabel="May 13, 2026" endLabel="This week" />
            : <Empty icon="sparkles" title="No AI Assistant sessions yet">GA4 started classifying this channel on May 13, 2026. Sessions appear here as assistants send traffic.</Empty>}
        </Card>
      </div>

      <Card padding="lg">
        <div className="dash-panelhead">
          <div><h2>Channels</h2><p>GA4 default channel group, with GA4 conversion rate per channel</p></div>
          {channels.length > 0 && <Button variant="ghost" size="sm" iconLeft={<Icon name="download" size={14} />} onClick={() => csvDownload('channels.csv', ['Channel', 'Sessions', 'Conversions', 'Conv rate %'], channels.map((c) => [c.name, Math.round(c.sessions), Math.round(c.conv), c.rate.toFixed(1)]))}>Export CSV</Button>}
        </div>
        {channels.length === 0 ? (
          <Empty title="No channel data">No sessions in this range for the selected segment.</Empty>
        ) : (
          <table className="tq-table">
            <thead><tr><th>Channel</th><th className="tq-num">Sessions</th><th>Share</th><th className="tq-num">Conversions</th><th className="tq-num">Conv. rate</th></tr></thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.name}>
                  <td><span className="tq-chname"><i className={`tq-dot tone-${c.tone}`} />{c.name}{c.isNew && <Badge variant="accent">New</Badge>}</span></td>
                  <td className="tq-num tq-mono">{int(c.sessions)}</td>
                  <td className="tq-barcell"><Bar pct={(c.sessions / chMax) * 100} tone={c.tone} /></td>
                  <td className="tq-num tq-mono">{int(c.conv)}</td>
                  <td className="tq-num tq-mono tq-strong">{c.rate.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="tq-two">
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>GA4 conversion events</h2><p>Key events (conversions) tracked in GA4 — Google Analytics' event counts, not Captured Leads</p></div></div>
          {events.length === 0 ? (
            <Empty icon="phone" title="No key events tracked yet">In GA4 → Admin → Events, mark this property&rsquo;s lead events (form submits, calls, chats, bookings) as <em>key events</em> — whatever they&rsquo;re named — and they&rsquo;ll appear here with their counts.</Empty>
          ) : (
            <ul className="tq-events">
              {events.map((e) => (
                <li key={e.key}>
                  <span className="tq-events__icon"><Icon name={EVENT_ICON[e.key] ?? 'check-circle'} size={16} /></span>
                  <span className="tq-events__txt"><strong>{e.name}</strong><em>conversions</em></span>
                  <span className="tq-events__n">{int(e.count)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card padding="lg">
          {/* Called "Service areas" until clients started asking why Triton was
              targeting Chicago. It is GA4's city dimension — where visitors are,
              not where the business works — and the two are not the same list. */}
          <div className="dash-panelhead">
            <div><h2>Sessions by city</h2><p>Where Google Analytics places your visitors</p></div>
            <div className="tq-seg" role="group" aria-label="City view">
              <button type="button" className={cityView === 'traffic' ? 'is-on' : ''} onClick={() => setCityView('traffic')}>Top traffic</button>
              <button type="button" className={cityView === 'leads' ? 'is-on' : ''} onClick={() => setCityView('leads')}>
                With leads{leadCities.length ? ` (${leadCities.length})` : ''}
              </button>
            </div>
          </div>
          {shownCities.length === 0 ? (
            <Empty icon="map-pin" title={cityView === 'leads' ? 'No cities with leads' : 'No city data'}>
              {cityView === 'leads'
                ? 'No city recorded a lead in this range. Switch to Top traffic to see where visitors came from.'
                : 'No geographic sessions in this range.'}
            </Empty>
          ) : (
            <>
              <ul className="tq-cities">
                {shownCities.map((c) => (
                  <li key={c.city}>
                    <span className="tq-cities__name"><Icon name="map-pin" size={14} />{c.city}</span>
                    <Bar pct={(c.sessions / cityMax) * 100} tone="organic" />
                    <span className="tq-cities__n">{int(c.sessions)}<em>{int(c.conv)} leads</em></span>
                  </li>
                ))}
              </ul>
              {cityView === 'leads' && unattributed > 0 && (
                <ul className="tq-cities">
                  <li className="tq-cities__rest">
                    <span className="tq-cities__name"><Icon name="alert-circle" size={14} />Not attributed to a city</span>
                    <span className="tq-cities__n">—<em>{int(unattributed)} conversions</em></span>
                  </li>
                </ul>
              )}
              {/* Said on the card rather than in a tooltip: the questions it
                  answers ("why is Chicago here?", "why don't these add up?") are
                  asked while looking at it. */}
              <p className="tq-note"><Icon name="alert-circle" size={14} /> Google Analytics estimates these from each visitor's IP address, so they are approximate. VPNs, corporate networks and mobile carriers routing through a regional hub can place a local visitor in another city, and bot traffic lands here too. This is visitor location, not your service area.
                {cityView === 'leads' && unattributed > 0 && ' GA4 also withholds city rows with too few visitors to report without identifying someone, so the cities above will not add up to your GA4 Conversions total — the difference is shown as unattributed.'}
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// Clean, short display name for a GA4 AI source host (e.g. gemini.google.com → Gemini).
function aiSourceLabel(host: string): string {
  const h = (host || '').toLowerCase();
  if (h.includes('chatgpt') || h.includes('openai')) return 'ChatGPT';
  if (h.includes('gemini') || h.includes('bard')) return 'Gemini';
  if (h.includes('perplexity')) return 'Perplexity';
  if (h.includes('copilot') || h.includes('edgeservices.bing')) return 'Copilot';
  if (h.includes('claude') || h.includes('anthropic')) return 'Claude';
  if (h.includes('deepseek')) return 'DeepSeek';
  if (h.includes('grok') || h.includes('x.ai')) return 'Grok';
  if (h.includes('meta.ai')) return 'Meta AI';
  if (h.includes('mistral')) return 'Mistral';
  return host.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

function AiTab({ d, range }: { d: TrafficPayload; range: string }) {
  const ai = d.ai!;
  const aiMax = Math.max(1, ...ai.sources.map((s) => s.sessions));
  // Server-log crawler telemetry — present only for clients whose site runs the
  // edge middleware (configured=false renders nothing; feature is opt-in).
  const { data: crawl } = useQuery({ queryKey: ['aiCrawl'], queryFn: getAiCrawl, staleTime: 5 * 60_000 });
  const { data: gsc } = useQuery({ queryKey: [...qk.search, range], queryFn: () => getSearchConsole(range), staleTime: 5 * 60_000 });
  const g = gsc as SearchPayload | undefined;
  const gscOn = !!g?.configured && !g?.error && !!g?.totals;
  return (
    <div className="dash-stack">
      <div className="tq-alert">
        <span className="tq-alert__icon"><Icon name="alert-circle" size={18} /></span>
        <div className="tq-alert__body">
          <strong>AI conversion tracking has known limitations</strong>
          <p>Analytics can tell when someone finds you through an AI tool like ChatGPT or Gemini. But if that person then calls you directly, we usually can&rsquo;t see that the call came from AI — so those callers stay invisible to our tracking and won&rsquo;t show up as AI leads here. The same goes for &ldquo;tap to call&rdquo; buttons inside Google&rsquo;s AI answers. So this number shows AI interest and reach; the AI leads we can actually confirm will be lower.</p>
        </div>
      </div>

      <div className="dash-kpis">
        <Card padding="lg"><Stat label="AI Assistant sessions" value={int(ai.sessions)} sub={`${ai.share.toFixed(1)}% of all sessions`} /></Card>
        <Card padding="lg"><Stat label="AI Assistant leads" value={int(ai.leads)} sub={`${ai.convRate.toFixed(1)}% conversion rate`} /></Card>
        <Card padding="lg"><Stat label="Search impressions" value={gscOn ? int(g!.totals!.impressions) : '—'} sub={g && !g.configured ? 'Search Console — not connected' : g?.error ? 'Search Console — unavailable' : 'Search Console · all surfaces'} /></Card>
        <Card padding="lg"><Stat label="Avg engagement time" value={dur(ai.avgEngagement)} sub="site average" /></Card>
      </div>

      <div className="tq-two">
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>AI Assistant sources</h2><p>The five assistants GA4 recognizes natively</p></div><Badge variant="neutral">GA4 native</Badge></div>
          {ai.sources.length === 0 ? (
            <Empty icon="sparkles" title="No AI Assistant sessions yet">Once ChatGPT, Gemini, Copilot, Grok, or Deepseek send traffic, their sessions and conversion rates appear here.</Empty>
          ) : (
            <>
              <ul className="tq-aisrc">
                {ai.sources.map((s) => (
                  <li key={s.name}>
                    <span className="tq-aisrc__name" title={s.name}>{aiSourceLabel(s.name)}</span>
                    <Bar pct={(s.sessions / aiMax) * 100} tone="ai" />
                    <span className="tq-aisrc__n">{int(s.sessions)}<em>{s.rate.toFixed(1)}% conv.</em></span>
                  </li>
                ))}
              </ul>
              <p className="tq-note"><Icon name="alert-circle" size={14} /> Perplexity is not in the GA4 AI Assistant channel — those sessions land under Referral. Capturing them needs a custom channel group per property.</p>
            </>
          )}
        </Card>

        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>Search visibility</h2><p>Search Console — all Google surfaces</p></div><Badge variant="neutral">Search Console</Badge></div>
          {g && !g.configured ? (
            <Empty icon="alert-circle" title="Search Console not connected">
              Add this client&rsquo;s Search Console site URL in Admin → Clients → Settings, then grant the Triton service account access to the property. Search data appears here once both are in place.
            </Empty>
          ) : g?.error ? (
            <Empty icon="alert-circle" title="Search Console request failed">{g.error}</Empty>
          ) : gscOn ? (
            <>
              <div className="tq-gscstats">
                <div><em>Impressions</em><b>{int(g!.totals!.impressions)}</b></div>
                <div><em>Clicks</em><b>{int(g!.totals!.clicks)}</b></div>
                <div><em>Avg CTR</em><b>{(g!.totals!.ctr * 100).toFixed(1)}%</b></div>
                <div><em>Avg position</em><b>{g!.totals!.position.toFixed(1)}</b></div>
              </div>
              <p className="tq-note"><Icon name="alert-circle" size={14} /> Google doesn&rsquo;t break out AI Overview impressions in the API — this is total Search visibility across all surfaces.</p>
            </>
          ) : <div className="lead-none">Loading…</div>}
        </Card>
      </div>

      <Card padding="lg">
        <div className="dash-panelhead"><div><h2>Pages earning AI citations</h2><p>Top pages by search impressions · modeled</p></div><Badge variant="neutral">Search Console</Badge></div>
        {g && !g.configured ? (
          <Empty icon="quote" title="Connect Search Console">
            Add the client&rsquo;s Search Console site URL in Admin → Clients → Settings to surface the pages most likely to be cited by AI.
          </Empty>
        ) : g?.error ? (
          <Empty icon="alert-circle" title="Search Console unavailable">See the Search visibility card above for the error details.</Empty>
        ) : !gscOn ? (
          <div className="lead-none">Loading…</div>
        ) : (g!.pages?.length ?? 0) === 0 ? (
          <div className="lead-none">No page data for this range yet.</div>
        ) : (
          <>
            <ul className="tq-aisrc">
              {g!.pages!.map((p) => (
                <li key={p.key}>
                  <span className="tq-aisrc__name" title={p.key}>{p.key.replace(/^https?:\/\/[^/]+/, '') || '/'}</span>
                  <Bar pct={(p.impressions / Math.max(1, ...g!.pages!.map((x) => x.impressions))) * 100} tone="ai" />
                  <span className="tq-aisrc__n">{int(p.impressions)}<em>{(p.ctr * 100).toFixed(1)}% CTR</em></span>
                </li>
              ))}
            </ul>
            <p className="tq-note"><Icon name="alert-circle" size={14} /> <em>Modeled</em> — Google&rsquo;s API doesn&rsquo;t expose AI citations directly, so this ranks your pages by overall search impressions (the pages AI is most likely to surface). Real AI-citation data will slot in when Google exposes it.</p>
          </>
        )}
      </Card>

      {crawl?.configured && <AiCrawlPanel crawl={crawl} />}
    </div>
  );
}

/* Server-log AI crawler activity — MEASURED, not modeled: the site's edge
   middleware records every verified AI bot hit. Shown only for clients whose
   site runs the middleware. Spoofed requests (verified=0) are excluded;
   window is rolling 30 days because live fetches are lumpy. */
function AiCrawlPanel({ crawl }: { crawl: AiCrawlReport }) {
  const s = crawl.stats!;
  const live = crawl.livePages ?? [];
  const blind = crawl.blindSpots ?? [];
  const errs = crawl.botErrors ?? [];
  const liveMax = Math.max(1, ...live.map((p) => p.count));
  const vendorLabel = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean)
    .map((x) => ({ openai: 'ChatGPT', anthropic: 'Claude', perplexity: 'Perplexity', mistral: 'Mistral', duckduckgo: 'DuckDuckGo', google: 'Google' }[x] ?? x)).join(', ');
  return (
    <>
      <div className="dash-kpis">
        <Card padding="lg"><Stat label="AI answer fetches" value={int(s.live30)} sub={`pages opened live while answering · ${crawl.windowDays}d`} info="A verified AI service (ChatGPT, Claude, Perplexity, Mistral) opened a page in real time while composing an answer to a user's question. Measured from this site's own server log — GA4 can't see these." /></Card>
        <Card padding="lg"><Stat label="Index crawls" value={int(s.index30)} sub={`${crawl.windowDays}d · AI search indexing`} info="AI search crawlers taking pages into the retrieval index answers draw from. A page absent from the index can't be cited from it." /></Card>
        <Card padding="lg"><Stat label="Pages in AI indexes" value={int(s.pagesIndexed)} sub="distinct pages ever index-crawled" /></Card>
        <Card padding="lg"><Stat label="AI-referred leads" value={int(s.aiLeads30)} sub={`captured leads from AI tools · ${crawl.windowDays}d`} info="Leads whose UTM/referrer shows they arrived from an AI assistant. The confirmed floor — referrer stripping pushes some AI traffic into Direct." /></Card>
      </div>

      <Card padding="lg">
        <div className="dash-panelhead">
          <div><h2>Pages AI opened while answering questions</h2><p>Live prompt-time fetches, verified by vendor IP · rolling {crawl.windowDays} days</p></div>
          <Badge variant="accent" dot>Measured · server log</Badge>
        </div>
        {live.length === 0 ? (
          <Empty icon="sparkles" title="No live AI fetches yet">
            Live fetches are rare and lumpy — a page might see a handful a month. Index coverage above is the leading indicator; fetches appear here as AI assistants open pages for real questions.
          </Empty>
        ) : (
          <>
            <ul className="tq-aisrc">
              {live.map((p) => (
                <li key={p.path}>
                  <span className="tq-aisrc__name" title={p.path}>{p.path}</span>
                  <Bar pct={(p.count / liveMax) * 100} tone="ai" />
                  <span className="tq-aisrc__n">{int(p.count)}<em>{vendorLabel(p.vendors)}{p.aiLeads ? ` · ${p.aiLeads} AI lead${p.aiLeads === 1 ? '' : 's'}` : ''}</em></span>
                </li>
              ))}
            </ul>
            <p className="tq-note"><Icon name="alert-circle" size={14} /> A fetch means the page was <em>considered</em> while an AI composed an answer — the honest claim is &ldquo;AI opened these pages answering questions,&rdquo; not &ldquo;cited N times.&rdquo; Most citations serve from the pre-built index, so absence here isn&rsquo;t absence from answers. AI-lead counts join by landing page.</p>
          </>
        )}
      </Card>

      {(blind.length > 0 || errs.length > 0) && (
        <div className="tq-two">
          <Card padding="lg">
            <div className="dash-panelhead"><div><h2>Blind spots</h2><p>Opened live but never index-crawled — fix list</p></div><Badge variant="warning">Action</Badge></div>
            {blind.length === 0 ? <div className="lead-none">None — everything AI reads is also being indexed.</div> : (
              <ul className="tq-aisrc">
                {blind.map((p) => (
                  <li key={p.path}>
                    <span className="tq-aisrc__name" title={p.path}>{p.path}</span>
                    <span className="tq-aisrc__n">{int(p.count)}<em>live fetches</em></span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card padding="lg">
            <div className="dash-panelhead"><div><h2>Errors served to AI bots</h2><p>404s/5xx — pages that silently can&rsquo;t be cited</p></div><Badge variant="warning">Action</Badge></div>
            {errs.length === 0 ? <div className="lead-none">None — every AI request got a good response.</div> : (
              <ul className="tq-aisrc">
                {errs.map((p) => (
                  <li key={`${p.path}-${p.status}`}>
                    <span className="tq-aisrc__name" title={p.path}>{p.path}</span>
                    <span className="tq-aisrc__n">{int(p.count)}<em>HTTP {p.status}</em></span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

/* One lead-source page: a ranked bar row that expands to the leads submitted from
   that page, each opening the lead drawer. Lead-first layout — the URL gets its
   own muted line so length never crowds the metric. */
function LeadPageRow({ pg, max, leadByUid, onLead }: { pg: LeadPageStat; max: number; leadByUid: Map<string, LeadItem>; onLead: (uid: string) => void }) {
  const [open, setOpen] = useState(false);
  const leads = pg.leadUids.map((u) => leadByUid.get(u)).filter((l): l is LeadItem => !!l);
  return (
    <li className="tq-leadpg">
      <button type="button" className="tq-leadpg__hd" onClick={() => setOpen((o) => !o)}>
        <span className="tq-leadpg__name">
          <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} />
          <b>{pageLabel(pg.path)}</b>
          <span>{pg.path}</span>
        </span>
        <span className="tq-leadpg__bar"><i style={{ width: `${(pg.leads / max) * 100}%` }} /></span>
        <span className="tq-leadpg__n">{pg.leads}</span>
      </button>
      {open && (
        <div className="tq-leadpg__body">
          {leads.length === 0 ? (
            <p className="dash-muted" style={{ padding: '4px 0' }}>Loading lead details…</p>
          ) : (
            <table className="dash-table">
              <thead><tr><th>Lead</th><th>AI quality</th><th>Value</th><th>Status</th><th>Received</th></tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.uid} className="cq-item--clickable" onClick={() => onLead(l.uid)}>
                    <td>{l.name || '—'}</td>
                    <td className="tq-num">{l.score ? l.score.toFixed(0) : '—'}</td>
                    <td className="tq-num">{l.value != null ? money2(l.value) : '—'}</td>
                    <td><Badge variant={STATUS_META[l.status as LeadStatus]?.variant ?? 'neutral'}>{STATUS_META[l.status as LeadStatus]?.label ?? l.status}</Badge></td>
                    <td className="tq-mono">{l.receivedAt ? l.receivedAt.slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </li>
  );
}

function LeadSourcesCard({ range, leadByUid, onLead }: { range: string; leadByUid: Map<string, LeadItem>; onLead: (uid: string) => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['leadPages', range], queryFn: () => getLeadPages(range), staleTime: 5 * 60_000 });
  const pages = data?.pages ?? [];
  const max = Math.max(1, ...pages.map((p) => p.leads));
  return (
    <Card padding="lg">
      <div className="dash-panelhead"><div><h2>Lead sources by page</h2><p>Where your leads came from — the page each lead was submitted from. Click a page to see its leads.</p></div></div>
      {isLoading ? (
        <p className="dash-muted">Loading…</p>
      ) : pages.length === 0 ? (
        <Empty icon="users" title="No lead sources yet">No leads with a referring page in this range.</Empty>
      ) : (
        <ul className="tq-leadpages">
          {pages.map((pg) => <LeadPageRow key={pg.path} pg={pg} max={max} leadByUid={leadByUid} onLead={onLead} />)}
        </ul>
      )}
      {data && data.total > 0 && (
        <p className="tq-note"><Icon name="alert-circle" size={14} /> {data.matched} of {data.total} leads ({Math.round((data.matched / data.total) * 100)}%) matched to a page by referring URL; the rest had none recorded. See Idea C for exact attribution.</p>
      )}
    </Card>
  );
}

function PagesTab({ d, range }: { d: TrafficPayload; range: string }) {
  const qc = useQueryClient();
  const pages = d.pages ?? [];
  const device = d.device ?? [];
  const devTotal = Math.max(1, device.reduce((s, x) => s + x.sessions, 0));
  const nr = d.newReturning ?? [];
  const nrTotal = Math.max(1, nr.reduce((s, x) => s + x.users, 0));

  const { data: leads = [] } = useQuery({ queryKey: qk.leads, queryFn: getLeads });
  const leadByUid = useMemo(() => new Map(leads.map((l) => [l.uid, l] as const)), [leads]);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const activeLead = activeUid ? leadByUid.get(activeUid) ?? null : null;
  const setStatus = (uid: string, status: LeadStatus) => { void setLeadStatus(uid, status).then(() => qc.invalidateQueries({ queryKey: qk.leads })); };
  const saveValue = (uid: string, value: number | null) => { void setLeadValue(uid, value).then(() => qc.invalidateQueries({ queryKey: qk.leads })); };

  return (
    <div className="dash-stack">
      <Card padding="lg">
        <div className="dash-panelhead">
          <div><h2>Landing pages</h2><p>Entry pages ranked by sessions</p></div>
          {pages.length > 0 && <Button variant="ghost" size="sm" iconLeft={<Icon name="download" size={15} />} onClick={() => csvDownload('landing-pages.csv', ['Page', 'Sessions', 'Avg engagement (s)', 'Conversions', 'Conv rate %'], pages.map((p) => [p.page, Math.round(p.sessions), Math.round(p.engSeconds), Math.round(p.conv), p.sessions ? ((p.conv / p.sessions) * 100).toFixed(1) : '0']))}>Export CSV</Button>}
        </div>
        {pages.length === 0 ? (
          <Empty title="No landing-page data">No entry-page sessions in this range.</Empty>
        ) : (
          <table className="tq-table">
            <thead><tr><th>Landing page</th><th className="tq-num">Sessions</th><th className="tq-num">Avg. engagement</th><th className="tq-num">Conversions</th><th className="tq-num">Conv. rate</th></tr></thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.page}>
                  <td className="tq-page"><span className="tq-page__url" title={p.page}>{p.page}</span></td>
                  <td className="tq-num tq-mono">{int(p.sessions)}</td>
                  <td className="tq-num tq-mono">{dur(p.engSeconds)}</td>
                  <td className="tq-num tq-mono">{int(p.conv)}</td>
                  <td className="tq-num tq-mono">{p.sessions ? ((p.conv / p.sessions) * 100).toFixed(1) : '0.0'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <LeadSourcesCard range={range} leadByUid={leadByUid} onLead={setActiveUid} />

      <div className="tq-two">
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>Device</h2><p>Home service traffic skews mobile and urgent</p></div></div>
          {device.length === 0 ? <Empty icon="smartphone" title="No device data">No sessions in this range.</Empty> : (
            <ul className="tq-cities">
              {device.map((x) => (
                <li key={x.name}>
                  <span className="tq-cities__name"><Icon name={DEVICE_ICON[x.name] ?? 'monitor'} size={14} />{x.name.charAt(0).toUpperCase() + x.name.slice(1)}</span>
                  <Bar pct={(x.sessions / devTotal) * 100} tone="organic" />
                  <span className="tq-cities__n">{int(x.sessions)}<em>{((x.sessions / devTotal) * 100).toFixed(1)}%</em></span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>New vs. returning</h2><p>Repeat visits signal maintenance-plan intent</p></div></div>
          {nr.length === 0 ? <Empty icon="users" title="No user data">No users in this range.</Empty> : (
            <ul className="tq-cities">
              {nr.map((x) => (
                <li key={x.name}>
                  <span className="tq-cities__name"><Icon name="users" size={14} />{x.name === 'new' ? 'New users' : x.name === 'returning' ? 'Returning' : x.name}</span>
                  <Bar pct={(x.users / nrTotal) * 100} tone="paid" />
                  <span className="tq-cities__n">{int(x.users)}<em>{((x.users / nrTotal) * 100).toFixed(1)}%</em></span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <LeadDrawer lead={activeLead} onClose={() => setActiveUid(null)} onSetStatus={setStatus} onSaveValue={saveValue} />
    </div>
  );
}

const CHANGE_ICON: Record<string, string> = { up: 'arrow-up-right', down: 'arrow-down-right', flat: 'arrow-right' };

function InsightsTab({ range }: { range: string }) {
  const qc = useQueryClient();
  const key = [...qk.whatChanged, range];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getWhatChanged(range), staleTime: 5 * 60_000 });
  const refresh = useMutation({ mutationFn: () => getWhatChanged(range, true), onSuccess: (d) => qc.setQueryData(key, d) });
  const d = data as WhatChanged | undefined;
  const rangeLabel = range === '7d' ? 'the last 7 days' : range === '90d' ? 'the last 90 days' : range === 'ytd' ? 'year to date' : 'the last 30 days';

  return (
    <div className="dash-stack">
      <Card padding="lg" className="ti-panel">
        <div className="ti-head">
          <div className="ti-title">
            <span className="ti-mark"><Icon name="sparkles" size={16} /></span>
            <div><h2>What changed</h2><p className="dash-muted">{rangeLabel} vs the period before</p></div>
          </div>
          <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending || isLoading} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {isLoading ? (
          <p className="dash-muted">Comparing this period to the last…</p>
        ) : !d || !d.configured ? (
          <Empty icon="trending-up" title="GA4 not connected">Connect this client&rsquo;s GA4 property to see what changed period over period.</Empty>
        ) : d.error ? (
          <p className="dash-muted">{d.error.includes('ANTHROPIC') ? 'The AI key isn’t configured on the Worker.' : 'Couldn’t analyze changes right now — try Refresh.'}</p>
        ) : d.empty ? (
          <p className="dash-muted">Not enough traffic yet to compare against the previous period.</p>
        ) : (
          <>
            {d.summary && <p className="ti-summary">{d.summary}</p>}
            {(d.changes?.length ?? 0) > 0 && (
              <ul className="tq-changes">
                {d.changes!.map((ch, i) => (
                  <li key={i}>
                    <span className={`tq-change__dir is-${ch.direction}`}><Icon name={CHANGE_ICON[ch.direction] ?? 'arrow-right'} size={15} /></span>
                    <span className="tq-change__metric">{ch.metric}</span>
                    <span className={`tq-change__delta is-${ch.direction}`}>{ch.delta}</span>
                    <span className="tq-change__note">{ch.note}</span>
                  </li>
                ))}
              </ul>
            )}
            {d.generatedAt && <p className="ti-stamp">Analysis from {minsAgo(d.generatedAt)} · updates daily · Refresh for the latest</p>}
          </>
        )}
      </Card>
    </div>
  );
}

/* ---- toolbar + shell -------------------------------------------------- */
function Toolbar({
  range, setRange, seg, setSeg, compareMode, setCompareMode, canCompare, d,
}: {
  range: string; setRange: (v: string) => void; seg: string; setSeg: (v: string) => void;
  compareMode: CompareMode; setCompareMode: (v: string) => void; canCompare: boolean; d?: TrafficPayload;
}) {
  const srcClass = d?.error ? 'is-error' : '';
  return (
    <div className="lead-toolbar">
      <div className="lead-toolbar__filters">
        <label className="lead-field"><Icon name="calendar" size={15} />
          <Select options={RANGE_OPTS} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range" />
        </label>
        <label className="lead-field"><Icon name="filter" size={15} />
          <Select options={SEGMENT_OPTS} value={seg} onChange={(e) => setSeg(e.target.value)} aria-label="Segment" />
        </label>
        {canCompare && (
          <label className="lead-field"><Icon name="trending-up" size={15} />
            <Select options={COMPARE_OPTS} value={compareMode} onChange={(e) => setCompareMode(e.target.value)} aria-label="Compare to period" />
          </label>
        )}
      </div>
      <div className="lead-toolbar__right">
        {d?.configured && (
          <span className={`tq-src ${srcClass}`.trim()}>
            <i className="tq-src__dot" />
            {d.error ? 'GA4 error' : `GA4 property ${fmtProperty(d.propertyId)}`}
            <em>{d.error ? d.error.slice(0, 40) : `synced ${minsAgo(d.fetchedAt)}`}</em>
          </span>
        )}
      </div>
    </div>
  );
}

function SkeletonTab() {
  return (
    <div className="dash-stack">
      <div className="dash-kpis">{[0, 1, 2, 3].map((i) => <div key={i} className="tq-skel" style={{ height: 96 }} />)}</div>
      <div className="tq-two"><div className="tq-skel" style={{ height: 160 }} /><div className="tq-skel" style={{ height: 160 }} /></div>
      <div className="tq-skel" style={{ height: 260 }} />
    </div>
  );
}

export function TrafficIQ() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const range = params.get('range') ?? '30d';
  const seg = params.get('seg') ?? 'all';
  const cmpRaw = params.get('compare');
  /* Period comparison is admin-only unless the client is opted in (Admin →
     client settings). When it is off for this viewer the control is hidden AND
     the mode is forced off — hiding the control alone would leave deltas on the
     page with no way to turn them off, and a hand-typed ?compare= would
     reintroduce them. Unknown access (still loading) counts as off, so a client
     never sees the comparison flash in before the gate resolves. */
  const { access } = useClientAccess();
  const canCompare = access?.canCompare ?? false;
  // Back-compat with the old boolean toggle ('0' = off, '1' = on/previous).
  const compareMode: CompareMode = !canCompare ? 'off'
    : cmpRaw === 'off' || cmpRaw === '0' ? 'off' : cmpRaw === 'yoy' ? 'yoy' : 'prev';
  const basis: CompareBasis = compareMode === 'yoy' ? 'yoy' : 'prev';
  const live = compareMode !== 'off';

  const setParam = (key: string, value: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['traffic', range, seg, basis],
    queryFn: () => getTraffic(range, seg, basis),
    staleTime: 5 * 60 * 1000,
  });

  const body = useMemo(() => {
    if (isLoading) return <SkeletonTab />;
    if (isError) return <Empty icon="alert-circle" title="Couldn’t load analytics">{error instanceof Error ? error.message : 'Request failed.'}</Empty>;
    if (!data) return null;
    if (!data.configured) {
      return (
        <Empty icon="trending-up" title="GA4 not connected for this client">
          Add this client&rsquo;s GA4 property ID in Admin → Clients, then grant the Triton service account Viewer access on the property. Analytics appear here once both are in place.
        </Empty>
      );
    }
    if (data.error && !data.kpis) {
      return <Empty icon="alert-circle" title="GA4 request failed">{data.error}</Empty>;
    }
    if (tab === 'overview') return <OverviewTab d={data} live={live} range={range} />;
    if (tab === 'ai') return <AiTab d={data} range={range} />;
    if (tab === 'pages') return <PagesTab d={data} range={range} />;
    return <InsightsTab range={range} />;
  }, [isLoading, isError, error, data, tab, live, range]);

  return (
    <div className="dash-stack">
      <div className="lead-tabsbar">
        <Tabs items={TABS} value={tab} onChange={(v) => setParam('tab', v)} variant="pill" />
      </div>
      <Toolbar
        range={range} setRange={(v) => setParam('range', v)}
        seg={seg} setSeg={(v) => setParam('seg', v)}
        compareMode={compareMode} setCompareMode={(v) => setParam('compare', v)}
        canCompare={canCompare}
        d={data}
      />
      {body}
    </div>
  );
}
